// Categories route — CRUD + per-category rules. Event-driven (no live-edit
// reactivity): structural changes reload; type changes are optimistic. Rules are
// lazy-loaded per category and cached. Uses the shared modal/confirm/toast UI
// primitives.
import { h, mount, type Child } from '../core/dom';
import { signal, computed, effect, type Signal } from '../core/signal';
import { text } from '../core/bind';
import {
	formatError,
	CATEGORY_TYPES,
	type Category,
	type CategoryType,
	type Rule,
} from '@lib/db';
import {
	loadCategories,
	loadRules,
	createCategory,
	updateCategory,
	deleteCategory,
	createRule,
	deleteRule,
} from '../data';
import { setHeaderControl } from '../components/headerSlot';
import { modal, confirmDialog } from '../ui/modal';
import { toast } from '../ui/toast';
import { loading } from '../ui/loading';

const byName = (a: Category, b: Category) => a.name.localeCompare(b.name);
const byTypeThenName = (a: Category, b: Category) =>
	(a.type || '').localeCompare(b.type || '') || a.name.localeCompare(b.name);
const typeLabel = (t: string) => t || 'Select type…';

export function categoriesRoute(): Node {
	const container = h('div', {});
	setHeaderControl(null);
	load();
	return container;

	function load(): void {
		container.replaceChildren(loading('Loading categories…'));
		loadCategories()
			.then(build)
			.catch((e) => container.replaceChildren(h('div', { class: 'state-error' }, formatError(e as Error))));
	}

	function build(loaded: Category[]): void {
		const cats = signal<Category[]>(loaded);
		const sortBy = signal<'category' | 'type'>('category');
		const expanded = signal<string | null>(null);
		// Rules are lazy-loaded per category: the signal exists from first read (so
		// the Rules button's count can subscribe), but the fetch only fires when the
		// category is first expanded (rulesRequested).
		const rulesCache = new Map<string, Signal<Rule[] | null>>();
		const rulesRequested = new Set<string>();
		const rulesFor = (name: string): Signal<Rule[] | null> => {
			let c = rulesCache.get(name);
			if (!c) {
				c = signal<Rule[] | null>(null);
				rulesCache.set(name, c);
			}
			return c;
		};
		const sorted = computed(() => [...cats()].sort(sortBy() === 'type' ? byTypeThenName : byName));

		function changeType(c: Category, type: CategoryType): void {
			cats.update((list) => list.map((x) => (x.name === c.name ? { ...x, type } : x)));
			updateCategory(c.name, { type }).catch((e) => {
				cats.update((list) => list.map((x) => (x.name === c.name ? { ...x, type: c.type } : x)));
				toast(formatError(e as Error));
			});
		}

		function doDelete(c: Category): void {
			confirmDialog(`Delete category "${c.name}"? Transactions keep their data.`, 'Delete').then((ok) => {
				if (!ok) return;
				deleteCategory(c.name).then(load).catch((e) => toast(formatError(e as Error)));
			});
		}

		function openRename(c: Category): void {
			const nameI = h('input', { class: 'input', value: c.name, ref: (el) => queueMicrotask(() => (el as HTMLInputElement).focus()) }) as HTMLInputElement;
			const typeSel = typeSelect(c.type ?? '');
			const close = modal({
				title: `Rename "${c.name}"`,
				body: [
					h('label', { class: 'row' }, 'Name', nameI),
					h('label', { class: 'row' }, 'Type', typeSel),
					h(
						'div',
						{ class: 'row spacer-end' },
						h('button', { class: 'btn btn-secondary', onclick: () => close() }, 'Cancel'),
						h(
							'button',
							{
								class: 'btn btn-primary',
								onclick: () => {
									const name = nameI.value.trim();
									if (!name) return;
									updateCategory(c.name, { name, type: typeSel.value as CategoryType })
										.then(() => {
											close();
											load();
										})
										.catch((e) => toast(formatError(e as Error)));
								},
							},
							'Save'
						)
					),
				],
			});
		}

		// The add-category controls live in the header bar (where month routes
		// show the switcher).
		setHeaderControl(addForm());

		container.replaceChildren(
			h(
				'div',
				{},
				h(
					'div',
					{ class: 'section-head' },
					h(
						'button',
						{ class: 'btn btn-sm btn-secondary spacer', onclick: () => sortBy.set(sortBy() === 'category' ? 'type' : 'category') },
						text(() => `Sort: ${sortBy()}`)
					)
				),
				listView()
			)
		);

		function addForm(): HTMLElement {
			const nameI = h('input', { class: 'input', placeholder: 'New category', 'aria-label': 'New category name' }) as HTMLInputElement;
			const typeSel = typeSelect('');
			const submit = () => {
				const name = nameI.value.trim();
				if (!name) return;
				createCategory(name, 0, typeSel.value as CategoryType)
					.then(load)
					.catch((e) => toast(formatError(e as Error)));
			};
			const form = h(
				'form',
				{ class: 'row', onsubmit: (e: SubmitEvent) => { e.preventDefault(); submit(); } },
				nameI,
				typeSel,
				h('button', { class: 'btn btn-primary', type: 'submit' }, 'Add')
			);
			return form;
		}

		function listView(): Child {
			const list = h('div', { class: 'cat-list' });
			mount(list, () => sorted().map((c) => catItem(c)));
			return list;
		}

		function catItem(c: Category): Child {
			const region = h('div', {});
			mount(region, () => (expanded() === c.name ? rulesView(c.name) : null));
			const typeSel = typeSelect(c.type ?? '');
			typeSel.addEventListener('change', () => changeType(c, typeSel.value as CategoryType));
			return h(
				'div',
				{ class: 'cat-item' },
				h(
					'div',
					{ class: 'cat-item-main' },
					h('span', { class: 'cat-name' }, c.name),
					typeSel,
					(() => {
						const btn = h(
							'button',
							{ class: 'btn btn-sm btn-secondary', onclick: () => expanded.set(expanded() === c.name ? null : c.name) },
							text(() => {
								const rs = rulesFor(c.name)();
								return rs?.length ? `Rules (${rs.length})` : 'Rules';
							})
						);
						effect(() => btn.setAttribute('aria-expanded', String(expanded() === c.name)));
						return btn;
					})(),
					h('button', { class: 'btn btn-sm btn-secondary', onclick: () => openRename(c) }, 'Edit'),
					h('button', { class: 'btn btn-sm btn-danger', onclick: () => doDelete(c) }, 'Delete')
				),
				region
			);
		}

		function rulesView(name: string): Child {
			const cache = rulesFor(name);
			if (!rulesRequested.has(name)) {
				rulesRequested.add(name);
				loadRules(name)
					.then((rs) => cache.set(rs))
					.catch((e) => {
						toast(formatError(e as Error));
						cache.set([]);
					});
			}
			const box = h('div', { class: 'rules-box' });
			mount(box, () => {
				const rules = cache();
				if (rules === null) return loading('Loading rules…');
				return [
					rules.length === 0
						? h('p', { class: 'muted' }, 'No rules.')
						: h('ul', { class: 'rules-list' }, ...rules.map((r) => ruleItem(name, r, cache))),
					addRuleForm(name, cache),
				];
			});
			return box;
		}

		function ruleItem(name: string, r: Rule, cache: Signal<Rule[] | null>): Child {
			return h(
				'li',
				{ class: 'rule-item' },
				h('code', {}, r.pattern),
				h('span', { class: 'badge' }, r.matchType),
				r.amountSign ? h('span', { class: 'badge' }, r.amountSign) : null,
				h(
					'button',
					{
						class: 'x-btn spacer',
						'aria-label': 'Delete rule',
						onclick: () => {
							deleteRule(name, r.sk.replace('RULE#', ''))
								.then(() => cache.update((rs) => (rs ?? []).filter((x) => x.sk !== r.sk)))
								.catch((e) => toast(formatError(e as Error)));
						},
					},
					'×'
				)
			);
		}

		function addRuleForm(name: string, cache: Signal<Rule[] | null>): Child {
			const patternI = h('input', { class: 'input', placeholder: 'Pattern', 'aria-label': 'Rule pattern' }) as HTMLInputElement;
			const matchSel = h('select', { class: 'select', 'aria-label': 'Match type' }, h('option', { value: 'contains' }, 'contains'), h('option', { value: 'regex' }, 'regex')) as HTMLSelectElement;
			const signSel = h('select', { class: 'select', 'aria-label': 'Amount sign' }, h('option', { value: '' }, 'Any amount'), h('option', { value: 'negative' }, 'Expenses only'), h('option', { value: 'positive' }, 'Income only')) as HTMLSelectElement;
			const submit = () => {
				const pattern = patternI.value.trim();
				if (!pattern) return;
				createRule(name, {
					pattern,
					matchType: matchSel.value as 'contains' | 'regex',
					amountSign: signSel.value as 'positive' | 'negative' | '',
				})
					.then((rule) => {
						cache.update((rs) => [...(rs ?? []), rule]);
						patternI.value = '';
					})
					.catch((e) => toast(formatError(e as Error)));
			};
			return h(
				'form',
				{ class: 'row', onsubmit: (e: SubmitEvent) => { e.preventDefault(); submit(); } },
				patternI,
				matchSel,
				signSel,
				h('button', { class: 'btn btn-sm btn-primary', type: 'submit' }, 'Add rule')
			);
		}
	}
}

function typeSelect(value: string): HTMLSelectElement {
	const sel = h('select', { class: 'select', 'aria-label': 'Category type' }) as HTMLSelectElement;
	for (const t of CATEGORY_TYPES) sel.append(h('option', { value: t }, typeLabel(t)));
	sel.value = value;
	return sel;
}
